import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { getTranslationLoadError, subscribeTranslationLoadError } from '../hooks/useTranslation';

/**
 * Sichtbarer Hinweis, wenn `/api/i18n/translations/<sprache>` nicht geladen
 * werden konnte.
 *
 * Ohne diesen Hinweis ist der Ausfall unsichtbar: die Anwendung rendert dann
 * überall die deutschen Default-Texte aus `t(key, default)` — in JEDER
 * Sprache. Für einen englischen oder französischen Nutzer sieht das nach einer
 * schlechten Übersetzung aus, nicht nach einem Fehler, und niemand meldet es.
 *
 * Der Text ist bewusst fest verdrahtet und zweisprachig (DE/EN) statt über
 * `t()` geführt: genau das Teilsystem, das hier übersetzen würde, ist ja
 * ausgefallen. `t()` lieferte den deutschen Default und damit einen
 * Pivot-Fallback, den Regel #7 verbietet. Das ist die dokumentierte Ausnahme
 * zu Regel #12 — nicht ein vergessener Key.
 */
export default function TranslationLoadErrorBanner() {
  const [fehler, setFehler] = useState(getTranslationLoadError);

  useEffect(() => subscribeTranslationLoadError(setFehler), []);

  if (!fehler) return null;

  return (
    <div
      role="alert"
      data-testid="i18n-load-error"
      data-i18n-error-language={fehler.language}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1001,
        background: 'var(--red-50)',
        borderBottom: '1px solid var(--red-200)',
        color: 'var(--red-800)',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
        fontSize: '1rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <AlertTriangle size={18} />
        <span>
          <strong>Übersetzungen konnten nicht geladen werden</strong>
          {' '}— die Seite zeigt gerade deutsche Ersatztexte.
        </span>
        <span style={{ opacity: 0.85 }}>
          <strong>Translations could not be loaded</strong>
          {' '}— this page is showing German fallback text.
        </span>
        <code style={{ opacity: 0.75, fontSize: '1rem' }}>
          {fehler.language}: {fehler.reason}
        </code>
      </div>

      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'var(--red-800)',
          color: 'var(--weiss)',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          padding: '6px 12px',
          fontWeight: 600,
          fontSize: '1rem',
          cursor: 'pointer',
        }}
      >
        <RefreshCw size={14} />
        Neu laden / Reload
      </button>
    </div>
  );
}
